import SwitchTiny from "@/components/switch-tiny"
import { ScaleInnerCard300 } from "@/domains/design/components/scale-inner-card-300"
import { SettingsSectionLayout } from "@/domains/settings/components/settings-section-layout"
import type { SwapFallbackMode } from "@/domains/swap-aggregators/types"

export type SwapAggregator = {
  id: string
  name: string
  description: string
  icon: string
  enabled: boolean
}

export const SwapAggregatorsSection: React.FC<{
  aggregators: Array<SwapAggregator>
  fallbackMode: SwapFallbackMode
  onFallbackAggregatorSelect: (aggregatorId: string) => void
  onFallbackModeChange: (mode: SwapFallbackMode) => void
}> = ({ aggregators, fallbackMode, onFallbackAggregatorSelect, onFallbackModeChange }) => {
  const isMultiFallbackMode = fallbackMode === "multi"

  return (
    <SettingsSectionLayout
      title="External Provider"
      description="Scale checks its own liquidity first. This provider is used only when it offers the safer quote."
    >
      <div className="space-y-4">
        <div
          className="grid auto-rows-auto grid-cols-1 gap-4 sm:grid-cols-3"
          style={{ gridTemplateRows: "auto 1fr auto" }}
        >
          {aggregators.map((aggregator) => {
            return (
              <ScaleInnerCard300
                key={aggregator.id}
                className={`grid grid-rows-[subgrid] rounded-lg p-4 transition-opacity ${
                  isMultiFallbackMode ? "cursor-not-allowed opacity-45" : "cursor-pointer"
                }`}
                style={{ gridRow: "span 3" }}
                enabled={!isMultiFallbackMode && aggregator.enabled}
                onClick={() => {
                  if (!isMultiFallbackMode) {
                    onFallbackAggregatorSelect(aggregator.id)
                  }
                }}
              >
                <h3
                  className={`flex items-center gap-2 text-lg font-medium ${
                    isMultiFallbackMode ? "text-t300" : "text-t100"
                  }`}
                >
                  <img src={aggregator.icon} alt={aggregator.name} className="h-4 w-4" />
                  {aggregator.name}
                </h3>
                <p className="text-sm text-t200">{aggregator.description}</p>
                <div className="flex justify-start" onClick={(e) => e.stopPropagation()}>
                  <SwitchTiny
                    checked={aggregator.enabled}
                    id={aggregator.id}
                    label={!isMultiFallbackMode && aggregator.enabled ? "Selected" : ""}
                    className={isMultiFallbackMode ? "pointer-events-none opacity-60" : undefined}
                    classNameLabel="text-primary-300"
                    onCheckedChange={() => {
                      if (!isMultiFallbackMode) {
                        onFallbackAggregatorSelect(aggregator.id)
                      }
                    }}
                  />
                </div>
              </ScaleInnerCard300>
            )
          })}
        </div>

        <ScaleInnerCard300 className="rounded-lg p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-bold text-t100">Try all external providers</h3>
              <p className="mt-1 text-sm text-t200">
                When Scale cannot serve a route, quote every integrated backup and use the best result.
              </p>
            </div>
            <SwitchTiny
              checked={fallbackMode === "multi"}
              id="swap-fallback-mode"
              label=""
              onCheckedChange={(checked) => onFallbackModeChange(checked ? "multi" : "single")}
            />
          </div>
        </ScaleInnerCard300>
      </div>
    </SettingsSectionLayout>
  )
}

